// JobPage.tsx — full rewrite
//
// Architecture change vs previous version:
//
//  BEFORE:
//    - One adriva_getJobs() fetch at app level
//    - entityJobs = useMemo filter of that one array
//    - Entity dashboard/monitor could never refresh independently
//    - If app-level fetch failed, everything was broken
//
//  NOW:
//    - adriva_getJobs(appNm) fetches all jobs for the app on mount
//    - entityJobs = useMemo filter of appJobs by selectedEntity.entityId
//    - No separate per-entity fetch — simpler, no extra API needed
//    - Refresh reloads the full app jobs list

import React, {
  useState, useEffect, useMemo, useRef, useCallback, useReducer,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay, faChartLine, faChartBar, faLayerGroup,
  faChevronDown, faGlobe, faTimes, faCheckDouble, faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import JobExecutionPage from "./JobExecutionPage";
import JobMonitor       from "./JobMonitor";
import JobDashboard     from "./JobDashboard";
import { adriva_getJobs } from "../API";

// ─── Config ───────────────────────────────────────────────────────────────────
const MAX_VISIBLE_TABS  = 7;

// ─── Types ────────────────────────────────────────────────────────────────────
type TopLevel  = "app-dashboard" | "entity";
type EntityTab = "dashboard" | "execution" | "monitor";
type MonitorFilters = {
  statusFilter?:  string;
  jobTypeFilter?: string;
  selectedJobId?: string;
};


type Props = { appNm: string; dmData: any[]; tool?: string }   // tool = the application module;

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Sora:wght@400;500;600;700&display=swap');

.jp { display:flex; flex-direction:column; height:100%; background:#eef1f6; font-family:'Sora',sans-serif; }

.jp-nav {
  display:flex; align-items:stretch; background:#fff;
  border-bottom:2px solid #c8d4e0; flex-shrink:0; min-height:48px;
  padding:0 12px; gap:0;
}

.jp-app-btn {
  all:unset; box-sizing:border-box;
  display:flex; align-items:center; gap:7px; padding:0 16px;
  font-size:12.5px; font-weight:600; cursor:pointer; white-space:nowrap;
  color:#445566; border-bottom:3px solid transparent;
  transition:color .14s, border-color .14s, background .14s; flex-shrink:0;
}
.jp-app-btn:hover { color:#1e3a6e; background:#f5f7fa; }
.jp-app-btn.on    { color:#1e3a6e; border-bottom-color:#1e3a6e;  }

.jp-nav-sep { width:1px; background:#e0e7ef; margin:10px 4px; flex-shrink:0; }

.jp-etabs { display:flex; align-items:stretch; flex:1; min-width:0; overflow:visible; }

.jp-etab {
  all:unset; box-sizing:border-box;
  display:flex; align-items:center; gap:5px; padding:0 14px;
  font-size:12px; font-weight:500; cursor:pointer; white-space:nowrap;
  color:#445566; border-bottom:3px solid transparent;
  transition:all .14s; max-width:160px;
}
.jp-etab-label { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.jp-etab:hover { color:#1e3a6e; background:#f5f7fa; }
.jp-etab.on    { color:#1e3a6e; border-bottom-color:#319795; font-weight:600; background:#f0f8f8; }
.jp-etab.batch { color:#166534; border-bottom-color:#16a34a; background:#f0fdf4; }
.jp-etab.batch .jp-etab-label::before { content:"✓ "; font-weight:700; }

.jp-over { position:relative; display:flex; align-items:center; }
.jp-over-btn {
  all:unset; box-sizing:border-box;
  display:flex; align-items:center; gap:5px; padding:0 12px; height:100%;
  font-size:12px; font-weight:500; cursor:pointer; white-space:nowrap;
  color:#445566; border-bottom:3px solid transparent; transition:all .14s;
}
.jp-over-btn:hover { color:#1e3a6e; background:#f5f7fa; }
.jp-over-btn.on    { color:#1e3a6e; border-bottom-color:#319795; font-weight:600; }
.jp-over-num {
  font-size:9.5px; background:#dde7f5; color:#1e3a6e;
  padding:1px 6px; border-radius:99px; font-weight:700;
}
.jp-over-drop {
  position:absolute; top:calc(100% + 3px); left:0; z-index:400;
  background:#fff; border:1px solid #c8d4e0; border-radius:9px;
  box-shadow:0 10px 30px rgba(0,0,0,.12); min-width:200px; overflow:hidden;
}
.jp-over-row {
  display:flex; align-items:center; gap:8px; padding:9px 14px;
  font-size:12.5px; cursor:pointer; color:#1e2d3d; transition:background .1s;
}
.jp-over-row:hover   { background:#f5f7fa; }
.jp-over-row.on      { background:#dde7f5; color:#1e3a6e; font-weight:600; }
.jp-over-row.batch   { background:#f0fdf4; color:#166534; }

.jp-nav-right {
  display:flex; align-items:center; gap:6px;
  margin-left:auto; padding:0 8px; flex-shrink:0;
}
.jp-btn {
  all:unset; box-sizing:border-box;
  display:inline-flex; align-items:center; gap:5px;
  padding:5px 11px; border-radius:6px; cursor:pointer;
  font-family:'Sora',sans-serif; font-size:11.5px; font-weight:500;
  border:1px solid; transition:all .12s; white-space:nowrap;
}
.jp-btn-batch { color:#b45309; border-color:#fde68a; background:#fffbeb; }
.jp-btn-batch:hover { background:#fef3c7; border-color:#f59e0b; }
.jp-btn-plain { color:#445566; border-color:#c8d4e0; background:#fff; }
.jp-btn-plain:hover { border-color:#90aed0; color:#1e3a6e; background:#f5f7fa; }
.jp-btn-exit  { color:#991b1b; border-color:#fca5a5; background:#fef2f2; }
.jp-btn-exit:hover { background:#fee2e2; }

.jp-bbar {
  display:flex; align-items:center; gap:8px; flex-wrap:wrap;
  padding:6px 16px; background:#fffbeb; border-bottom:1px solid #fde68a;
  flex-shrink:0;
}
.jp-bbar-title { font-size:12px; font-weight:700; color:#92400e; }
.jp-bbar-count {
  font-size:10.5px; background:#fef3c7; color:#92400e;
  border:1px solid #fde68a; padding:1px 8px; border-radius:99px; font-weight:700;
}
.jp-bbar-hint { font-size:11px; color:#b45309; margin-left:auto; }

.jp-body { flex:1; min-height:0; display:flex; overflow:hidden; }

.jp-sidebar {
  width:54px; background:#fff; border-right:1px solid #c8d4e0;
  display:flex; flex-direction:column; align-items:center;
  padding:14px 0; gap:2px; flex-shrink:0;
}
.jp-side {
  all:unset; box-sizing:border-box;
  width:42px; height:42px; border-radius:8px; cursor:pointer;
  display:flex; flex-direction:column; align-items:center;
  justify-content:center; gap:3px; transition:all .14s; color:#445566;
}
.jp-side:hover { background:#eef1f6; color:#1e3a6e; }
.jp-side.on    { background:#dde7f5; color:#1e3a6e; }
.jp-side-lbl   { font-size:8px; font-weight:600; letter-spacing:.4px; text-transform:uppercase; line-height:1; }

.jp-pane { flex:1; min-width:0; overflow:hidden; display:flex; flex-direction:column; }

/* Entity loading / error states */
.jp-entity-loading {
  flex:1; display:flex; align-items:center; justify-content:center;
  gap:10px; color:#445566; font-size:13px; font-family:'Sora',sans-serif;
}
.jp-entity-error {
  margin:20px; padding:12px 16px; border-radius:8px;
  background:#fef2f2; border:1px solid #fca5a5; color:#991b1b;
  font-size:12.5px; font-family:'Sora',sans-serif;
  display:flex; align-items:center; gap:10px;
}

.jp-spin {
  width:16px; height:16px; border-radius:50%;
  border:2px solid #c8d4e0; border-top-color:#1e3a6e;
  animation:jpspin .7s linear infinite;
}
@keyframes jpspin { to { transform:rotate(360deg); } }

.jp-empty {
  flex:1; display:flex; flex-direction:column; align-items:center;
  justify-content:center; gap:10px; color:#9ca3af; text-align:center; padding:60px;
}
`;

// ─── Component ────────────────────────────────────────────────────────────────
const JobPage: React.FC<Props> = ({ appNm, dmData, tool }) => {
  const entities         = dmData ?? [];
  const visibleEntities  = entities.slice(0, MAX_VISIBLE_TABS);
  const overflowEntities = entities.slice(MAX_VISIBLE_TABS);

  // ── View state ────────────────────────────────────────────────────────────
  const [topLevel,         setTopLevel]         = useState<TopLevel>("app-dashboard");
  const [selectedEntityId, setSelectedEntityId] = useState<string>(entities[0]?.id ?? "");
  const [entityTab,        setEntityTab]        = useState<EntityTab>("dashboard");
  const [monitorFilters,   setMonitorFilters]   = useState<MonitorFilters>({});

  // ── App-level jobs (for App Dashboard only) ───────────────────────────────
  const [appJobs,       setAppJobs]       = useState<any[] | null>(null);
  const [appJobsLoading, setAppJobsLoading] = useState(false);

  // ── Entity-level jobs cache ───────────────────────────────────────────────
  // Keyed by entity.entityId (the backend ID, not entity.id)

  // ── Batch ─────────────────────────────────────────────────────────────────
  const [batchMode,     setBatchMode]     = useState(false);
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set());

  // ── Overflow ──────────────────────────────────────────────────────────────
  const [overOpen, setOverOpen] = useState(false);
  const overRef = useRef<HTMLDivElement>(null);

  // ── Derived ───────────────────────────────────────────────────────────────
  const selectedEntity = useMemo(
    () => entities.find(e => e.id === selectedEntityId) ?? null,
    [entities, selectedEntityId]
  );

  // Derive entity-specific jobs from appJobs (no extra API call needed)
  // appJobs has entity_id on every row from the /app/{nm}/jobs endpoint
  const entityJobs = useMemo(() => {
    if (!appJobs || !selectedEntity?.entityId) return null;
    return appJobs.filter((j: any) => j.entity_id === selectedEntity.entityId);
  }, [appJobs, selectedEntity?.entityId]);

  const overflowHasActive = topLevel === "entity" &&
    overflowEntities.some(e => e.id === selectedEntityId);

  const batchEntities = useMemo(
    () => entities.filter(e => batchSelected.has(e.id)),
    [entities, batchSelected]
  );

  // Current entity's cache entry

  // ── Load app-level jobs (App Dashboard) ──────────────────────────────────
  const loadAppJobs = useCallback(() => {
    if (!appNm) return;
    setAppJobsLoading(true);
    adriva_getJobs(
      appNm,
      (res: any) => { if (res.success) setAppJobs(res.data || []); setAppJobsLoading(false); },
      () => setAppJobsLoading(false)
    );
  }, [appNm]);

  useEffect(() => { loadAppJobs(); }, [loadAppJobs]);



  // ── Close overflow on outside click ──────────────────────────────────────
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (overRef.current && !overRef.current.contains(e.target as Node))
        setOverOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Navigation helpers ────────────────────────────────────────────────────
  const goEntity = useCallback((id: string) => {
    setSelectedEntityId(id);
    setTopLevel("entity");
    setOverOpen(false);
  }, []);

  const handleEntityClick = (id: string) => {
    if (batchMode) {
      setBatchSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    } else {
      goEntity(id);
    }
  };

  const drillToMonitor = useCallback((opts: MonitorFilters) => {
    setMonitorFilters(opts);
    setEntityTab("monitor");
  }, []);

  const drillToMonitorFromApp = useCallback((opts: MonitorFilters) => {
    const first = entities[0];
    if (first) {
      setSelectedEntityId(first.id);
      setTopLevel("entity");
      setMonitorFilters(opts);
      setEntityTab("monitor");
    }
  }, [entities]);

  // ── Batch helpers ─────────────────────────────────────────────────────────
  const enterBatch = () => {
    setBatchMode(true);
    setBatchSelected(new Set([selectedEntityId]));
    setTopLevel("entity");
    setEntityTab("execution");
  };
  const exitBatch = () => { setBatchMode(false); setBatchSelected(new Set()); };

  // ── Entity refresh (force reload) ─────────────────────────────────────────
  const refreshEntityJobs = useCallback(() => {
    loadAppJobs();
  }, [loadAppJobs]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="jp">
      <style>{CSS}</style>

      {/* ════════ TOP NAV ════════════════════════════════════════════════════ */}
      <div className="jp-nav">

        {/* App-level dashboard */}
        <button
          className={`jp-app-btn${topLevel === "app-dashboard" ? " on" : ""}`}
          onClick={() => { setTopLevel("app-dashboard"); setBatchMode(false); }}
          title="App-wide dashboard — all entities"
        >
          <FontAwesomeIcon icon={faGlobe} style={{ color: "#90aed0", fontSize: 13 }} />
          App Dashboard
          {appJobsLoading && <div style={{ width:10, height:10, borderRadius:"50%", border:"1.5px solid #c8d4e0", borderTopColor:"#1e3a6e", animation:"jpspin .7s linear infinite" }} />}
        </button>

        <div className="jp-nav-sep" />

        {/* Entity tabs */}
        <div className="jp-etabs">
          {visibleEntities.map(e => {
            const isOn    = topLevel === "entity" && selectedEntityId === e.id && !batchMode;
            const isBatch = batchMode && batchSelected.has(e.id);
            return (
              <button
                key={e.id}
                className={`jp-etab${isOn ? " on" : ""}${isBatch ? " batch" : ""}`}
                onClick={() => handleEntityClick(e.id)}
                title={e.label}
              >
                <span className="jp-etab-label">{e.label}</span>
              </button>
            );
          })}

          {/* Overflow */}
          {overflowEntities.length > 0 && (
            <div className="jp-over" ref={overRef}>
              <button
                className={`jp-over-btn${overflowHasActive ? " on" : ""}`}
                onClick={() => setOverOpen(o => !o)}
              >
                {overflowHasActive
                  ? <span style={{ maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {overflowEntities.find(e => e.id === selectedEntityId)?.label ?? "More"}
                    </span>
                  : "More"
                }
                <span className="jp-over-num">{overflowEntities.length}</span>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize:9 }} />
              </button>
              {overOpen && (
                <div className="jp-over-drop">
                  {overflowEntities.map(e => {
                    const isOn    = topLevel === "entity" && selectedEntityId === e.id;
                    const isBatch = batchMode && batchSelected.has(e.id);
                    return (
                      <div
                        key={e.id}
                        className={`jp-over-row${isOn ? " on" : ""}${isBatch ? " batch" : ""}`}
                        onClick={() => handleEntityClick(e.id)}
                      >
                        {isBatch && <span style={{ fontSize:11, color:"#16a34a", flexShrink:0 }}>✓</span>}
                        <span style={{ flex:1 }}>{e.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right controls */}
        <div className="jp-nav-right">
          {!batchMode ? (
            <button className="jp-btn jp-btn-batch" onClick={enterBatch}>
              <FontAwesomeIcon icon={faLayerGroup} /> Batch run
            </button>
          ) : (
            <>
              <button className="jp-btn jp-btn-plain" onClick={() => setBatchSelected(new Set(entities.map(e => e.id)))}>
                <FontAwesomeIcon icon={faCheckDouble} /> All
              </button>
              <button className="jp-btn jp-btn-plain" onClick={() => setBatchSelected(new Set())}>Clear</button>
              <button className="jp-btn jp-btn-exit" onClick={exitBatch}>
                <FontAwesomeIcon icon={faTimes} /> Exit batch
              </button>
            </>
          )}
        </div>
      </div>

      {/* ══════ Batch info bar ══════════════════════════════════════════════ */}
      {batchMode && (
        <div className="jp-bbar">
          <FontAwesomeIcon icon={faLayerGroup} style={{ color:"#d97706" }} />
          <span className="jp-bbar-title">Batch mode</span>
          <span className="jp-bbar-count">{batchSelected.size} selected</span>
          <span className="jp-bbar-hint">
            Click entity tabs to toggle · switch to Execution (▶) to run
          </span>
        </div>
      )}

      {/* ══════════════════ BODY ════════════════════════════════════════════ */}
      <div className="jp-body">

        {/* ── App Dashboard (full-width, no sidebar) ────────────────────── */}
        {topLevel === "app-dashboard" && (
          <div className="jp-pane">
            <JobDashboard
              jobs={appJobs}
              dmData={entities}
              onNavigateToMonitor={drillToMonitorFromApp}
              onRefresh={loadAppJobs}
            />
          </div>
        )}

        {/* ── Entity view (sidebar + content) ───────────────────────────── */}
        {topLevel === "entity" && (
          <>
            {/* Sidebar */}
            <div className="jp-sidebar">
              {(
                [
                  { key: "dashboard" as EntityTab, icon: faChartBar,  label: "Dash"  },
                  { key: "execution" as EntityTab, icon: faPlay,      label: "Run"   },
                  { key: "monitor"   as EntityTab, icon: faChartLine, label: "Mon"   },
                ] as { key: EntityTab; icon: any; label: string }[]
              ).map(({ key, icon, label }) => (
                <button
                  key={key}
                  className={`jp-side${entityTab === key ? " on" : ""}`}
                  onClick={() => setEntityTab(key)}
                  title={label}
                >
                  <FontAwesomeIcon icon={icon} style={{ fontSize:15 }} />
                  <span className="jp-side-lbl">{label}</span>
                </button>
              ))}

              {/* Refresh button for entity jobs — at bottom of sidebar */}
              <div style={{ marginTop:"auto", paddingBottom:8 }}>
                <button
                  className="jp-side"
                  title="Refresh entity jobs"
                  onClick={refreshEntityJobs}
                  style={{ opacity: appJobsLoading ? 0.5 : 1 }}
                >
                  <FontAwesomeIcon
                    icon={faRefresh}
                    style={{ fontSize:13, animation: appJobsLoading ? "jpspin .7s linear infinite" : "none" }}
                  />
                  <span className="jp-side-lbl">Reload</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="jp-pane">

              {/* ── Loading state ── */}
              {appJobsLoading && !entityJobs && (
                <div className="jp-entity-loading">
                  <div className="jp-spin" />
                  <span>Loading jobs for {selectedEntity?.label}…</span>
                </div>
              )}



              {/* ── Entity Dashboard ── */}
              {entityTab === "dashboard" && (entityJobs || !appJobsLoading) && (
                <JobDashboard
                  jobs={entityJobs}
                  dmData={selectedEntity ? [selectedEntity] : []}
                  onNavigateToMonitor={drillToMonitor}
                  onRefresh={refreshEntityJobs}
                />
              )}

              {/* ── Execution ── */}
              {entityTab === "execution" && (
                <JobExecutionPage
                  tool={tool}
                  appNm={appNm}
                  selectedEntity={selectedEntity}
                  selectedEntities={batchMode ? batchEntities : (selectedEntity ? [selectedEntity] : [])}
                  isBatchMode={batchMode}
                  entityJobs={entityJobs}
                />
              )}

              {/* ── Monitor ── */}
              {entityTab === "monitor" && (entityJobs !== undefined || !appJobsLoading) && (
                <JobMonitor
                  appNm={appNm}
                  entityName={selectedEntity?.label}
                  selectedEntityId={selectedEntityId}
                  initialJobs={entityJobs}
                  initialStatusFilter={monitorFilters.statusFilter}
                  initialJobTypeFilter={monitorFilters.jobTypeFilter}
                  initialSelectedJobId={monitorFilters.selectedJobId}
                  onRefresh={refreshEntityJobs}
                  onNavigateToRun={() => setEntityTab("execution")}
                />
              )}

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JobPage;